import { useEffect } from "react";

import { useMutation } from "@tanstack/react-query";

import { useApi } from "@/hooks";

// Module-scoped session-dedup of boost UUIDs that have already fired an
// impression. Lives outside the hook so every <Status> instance shares the
// same set — switching feeds, scrolling, or remounting the component does
// not re-POST for the same boost. Cleared only on full page reload (which
// is the right session boundary; sessionStorage isn't needed).
const FIRED_IMPRESSIONS = new Set<string>();

// Exposed for tests only. Production code must not reach in.
function __resetBoostImpressionDedup(): void {
  FIRED_IMPRESSIONS.clear();
}

/**
 * Reports a client impression for a server-injected boost.
 *
 * Fires POST /api/v1/boosts/:uuid/impression at most once per boost UUID per
 * page-load. Uses a module-scoped Set rather than relying on react-query's
 * cache lifetime (which is not a contract for at-most-once delivery: a
 * QueryClient reset on logout, devtools invalidation, or strict-mode double
 * invocation can re-fire a useQuery-as-side-effect).
 */
function useBoostImpression(boostId: string | null | undefined): void {
  const api = useApi();
  const { mutate } = useMutation((id: string) =>
    api.post(`/api/v1/boosts/${id}/impression`),
  );

  useEffect(() => {
    if (!boostId) return;
    if (FIRED_IMPRESSIONS.has(boostId)) return;
    FIRED_IMPRESSIONS.add(boostId);
    mutate(boostId);
  }, [boostId, mutate]);
}

export { useBoostImpression, __resetBoostImpressionDedup };
